import { useState, useRef } from 'react';
import LocationSelect from '../components/shared/LocationSelect.jsx';
import { apiFetch, useApi, getToken } from '../hooks/useApi.js';
import { uiConfirm } from '../utils/dialogs.js';

// ── Shared UI primitives ──────────────────────────────────────────────────────
function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = { borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer', border: 'none', fontSize: size === 'sm' ? 12 : 14, padding: size === 'sm' ? '5px 12px' : '9px 18px', opacity: props.disabled ? 0.5 : 1, transition: 'opacity .15s' };
  const v = { primary: { background: 'var(--pld-green)', color: '#fff' }, ghost: { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }, danger: { background: '#dc2626', color: '#fff' } };
  return <button {...props} style={{ ...base, ...v[variant], ...props.style }}>{children}</button>;
}

function Modal({ title, onClose, width = 540, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{label}</label>
      {children}
      {hint && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hint}</span>}
    </div>
  );
}

function Input(props) {
  return <input {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }} />;
}

function SelectEl({ children, ...props }) {
  return <select {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }}>{children}</select>;
}

function Textarea(props) {
  return <textarea {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', resize: 'vertical', minHeight: 72, ...props.style }} />;
}

function SectionHeader({ label, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{label}</h4>
      {action}
    </div>
  );
}

function Pill({ on, label }) {
  return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: on ? '#dcfce7' : '#f3f4f6', color: on ? '#166534' : '#6b7280' }}>{label}</span>;
}

function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 2, borderBottom: '2px solid var(--border)', marginBottom: 20 }}>
      {tabs.map(t => (
        <button key={t} onClick={() => onChange(t)} style={{ padding: '7px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: active === t ? 'var(--pld-green)' : 'var(--text-muted)', borderBottom: active === t ? '2px solid var(--pld-green)' : '2px solid transparent', marginBottom: -2 }}>
          {t}
        </button>
      ))}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = s => !s ? '—' : new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const INTAKE_STATUS_COLOURS = {
  pending:     { bg: '#fef9c3', text: '#854d0e' },
  checked_in:  { bg: '#dcfce7', text: '#166534' },
  discrepancy: { bg: '#fee2e2', text: '#991b1b' },
  cancelled:   { bg: '#f3f4f6', text: '#6b7280' },
};

function StatusBadge({ status, colours }) {
  const c = colours[status] ?? { bg: '#f3f4f6', text: '#6b7280' };
  return <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', background: c.bg, color: c.text }}>{status?.replace(/_/g, ' ')}</span>;
}

function initials(name = '') {
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

// ── Create / Edit Client Modal ────────────────────────────────────────────────
const BLANK_CLIENT = { name: '', company_number: '', vat_number: '', excise_id: '', nda_on_file: false, is_own_duty_payer: false, show_costs_to_client: false, payment_terms: '' };

export function ClientFormModal({ title, initial = BLANK_CLIENT, onClose, onSaved, existing, onOpenExisting }) {
  const [form, setForm] = useState(initial);
  const [contacts, setContacts] = useState(() => (initial.id ? [] : (initial.contacts || [])));
  const setContact = (i, patch) => setContacts(cs => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const res = initial.id
        ? await apiFetch(`/clients/${initial.id}`, { method: 'PATCH', body: form })
        : await apiFetch('/clients', { method: 'POST', body: { ...form, contacts: contacts.filter(c => (c.name || '').trim()) } });
      onSaved(res);
    } catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <Modal title={title} onClose={onClose} width={640}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {existing && (
          <div style={{ fontSize: 13, background: '#fef3c7', color: '#92400e', borderRadius: 'var(--radius)', padding: '9px 12px' }}>
            <strong>{existing.name}</strong>{existing.is_active === false ? ' (inactive)' : ''} is already in Blake (same {existing.matched_by}).
            {onOpenExisting && <> <button type="button" onClick={() => onOpenExisting(existing)} style={{ background: 'none', border: 'none', padding: 0, color: '#92400e', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}>Open them instead</button></>}
          </div>
        )}
        <Field label="Client Name *">
          <Input value={form.name} onChange={e => set('name', e.target.value)} required autoFocus />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Company Number">
            <Input value={form.company_number ?? ''} onChange={e => set('company_number', e.target.value)} placeholder="e.g. 12345678" />
          </Field>
          <Field label="VAT Number">
            <Input value={form.vat_number ?? ''} onChange={e => set('vat_number', e.target.value)} placeholder="e.g. GB123456789" />
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Excise ID">
            <Input value={form.excise_id ?? ''} onChange={e => set('excise_id', e.target.value)} placeholder="HMRC excise number" />
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Fulfilment fee per order (£)">
            <Input type="number" min="0" step="0.01" value={form.handling_fee_override ?? ''} onChange={e => set('handling_fee_override', e.target.value === '' ? null : e.target.value)} placeholder="Blank = the rate in Settings" />
          </Field>
          <Field label="Copy DPD tracking emails to">
            <Input type="email" value={form.dpd_notify_email ?? ''} onChange={e => set('dpd_notify_email', e.target.value)} placeholder="e.g. orders@client.com" />
          </Field>
        </div>
        <details open={!!(form.billing_address || form.delivery_address || form.awrs_number || form.bonded_warehouse_name || form.bank_iban)} style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Addresses, trade IDs and bank details</summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Billing address"><Textarea rows={2} value={form.billing_address ?? ''} onChange={e => set('billing_address', e.target.value)} /></Field>
              <Field label="Delivery address"><Textarea rows={2} value={form.delivery_address ?? ''} onChange={e => set('delivery_address', e.target.value)} /></Field>
            </div>
            <Field label="Delivery hours & forklift access"><Input value={form.delivery_notes ?? ''} onChange={e => set('delivery_notes', e.target.value)} placeholder="e.g. Mon–Fri 9–6, forklift on site" /></Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="AWRS number"><Input value={form.awrs_number ?? ''} onChange={e => set('awrs_number', e.target.value)} /></Field>
              <Field label="EORI"><Input value={form.eori_number ?? ''} onChange={e => set('eori_number', e.target.value)} /></Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Their bonded warehouse"><Input value={form.bonded_warehouse_name ?? ''} onChange={e => set('bonded_warehouse_name', e.target.value)} placeholder="Warehouse business name" /></Field>
              <Field label="Warehouse keeper ID"><Input value={form.warehouse_keeper_id ?? ''} onChange={e => set('warehouse_keeper_id', e.target.value)} /></Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="IBAN"><Input value={form.bank_iban ?? ''} onChange={e => set('bank_iban', e.target.value)} /></Field>
              <Field label="BIC"><Input value={form.bank_bic ?? ''} onChange={e => set('bank_bic', e.target.value)} /></Field>
            </div>
          </div>
        </details>
        {!initial.id && contacts.length > 0 && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Contacts ({contacts.length})</div>
            {contacts.map((c, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1.4fr 1fr 24px', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                <Input value={c.name} onChange={e => setContact(i, { name: e.target.value })} placeholder="Name" style={{ padding: '6px 9px', fontSize: 13 }} />
                <Input value={c.role || ''} onChange={e => setContact(i, { role: e.target.value })} placeholder="Role" style={{ padding: '6px 9px', fontSize: 13 }} />
                <Input value={c.email || ''} onChange={e => setContact(i, { email: e.target.value })} placeholder="Email" style={{ padding: '6px 9px', fontSize: 13 }} />
                <Input value={c.phone || ''} onChange={e => setContact(i, { phone: e.target.value })} placeholder="Phone" style={{ padding: '6px 9px', fontSize: 13 }} />
                <button type="button" onClick={() => setContacts(cs => cs.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18 }}>&#215;</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {[
            ['nda_on_file',          'NDA on file'],
            ['is_own_duty_payer',    'Own duty payer'],
            ['show_costs_to_client', 'Show costs to client'],
          ].map(([k, label]) => (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!form[k]} onChange={e => set(k, e.target.checked)} />
              {label}
            </label>
          ))}
        </div>
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || !form.name?.trim()}>{saving ? 'Saving…' : (initial.id ? 'Save Changes' : 'Create Client')}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// The blank New Customer Form to send to a customer; Import reads it back.
async function downloadCustomerForm() {
  try {
    const res = await fetch('/api/clients/form-template', { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) throw new Error(`Couldn't download the form (HTTP ${res.status})`);
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a'); a.href = url; a.download = 'PLD New Customer Form.docx'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { window.alert(e.message); }
}

// ── Import a New Contract Customer Form ─────────────────────────────────────
// Reads the form (PDF, Word or a photo), then opens the New Client form
// filled in, with its contacts, for checking before anything is saved.
export function ImportCustomerFormModal({ onClose, onSaved, onOpenExisting }) {
  const [file, setFile] = useState(null);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  async function read(e) {
    e.preventDefault(); setReading(true); setError('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch('/api/clients/read-form', { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Couldn't read it (HTTP ${res.status})`);
      setResult(body);
    } catch (err) { setError(err.message); setReading(false); }
  }
  if (result) {
    return (
      <ClientFormModal
        title="New Client — from customer form"
        initial={{ ...BLANK_CLIENT, ...result.client, contacts: result.contacts }}
        existing={result.existing}
        onOpenExisting={onOpenExisting}
        onClose={onClose}
        onSaved={onSaved}
      />
    );
  }
  return (
    <Modal title="Import a customer form" onClose={onClose} width={480}>
      <form onSubmit={read} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>Choose their filled-in New Customer Form (download a blank one from the Clients page) — PDF, Word (.docx) or a photo. Blake fills in the client, addresses, trade IDs and contacts for you to check. Nothing is saved until you press Create.</p>
        <Field label="Customer form"><input type="file" accept="application/pdf,.docx,image/*" onChange={e => setFile(e.target.files?.[0] ?? null)} required /></Field>
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={!file || reading}>{reading ? 'Reading…' : 'Read form'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Contact Form Modal ────────────────────────────────────────────────────────
const BLANK_CONTACT = { name: '', role: '', email: '', phone: '', is_primary: false, is_accounts: false, is_recall_contact: false };

function ContactFormModal({ clientId, initial = BLANK_CONTACT, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const res = initial.id
        ? await apiFetch(`/clients/${clientId}/contacts/${initial.id}`, { method: 'PATCH', body: form })
        : await apiFetch(`/clients/${clientId}/contacts`, { method: 'POST', body: form });
      onSaved(res);
    } catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <Modal title={initial.id ? 'Edit Contact' : 'Add Contact'} onClose={onClose} width={500}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field label="Name *">
          <Input value={form.name} onChange={e => set('name', e.target.value)} required autoFocus />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Role">
            <Input value={form.role ?? ''} onChange={e => set('role', e.target.value)} placeholder="e.g. Director" />
          </Field>
          <Field label="Phone">
            <Input value={form.phone ?? ''} onChange={e => set('phone', e.target.value)} placeholder="e.g. 07700 900123" />
          </Field>
        </div>
        <Field label="Email">
          <Input type="email" value={form.email ?? ''} onChange={e => set('email', e.target.value)} />
        </Field>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {[
            ['is_primary',        'Primary contact'],
            ['is_accounts',       'Accounts contact'],
            ['is_recall_contact', 'Recall contact'],
          ].map(([k, label]) => (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!form[k]} onChange={e => set(k, e.target.checked)} />
              {label}
            </label>
          ))}
        </div>
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || !form.name?.trim()}>{saving ? 'Saving…' : (initial.id ? 'Save' : 'Add Contact')}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Contacts Tab ──────────────────────────────────────────────────────────────
function ContactsTab({ clientId, initialContacts }) {
  const { data, loading, refetch } = useApi(`/clients/${clientId}/contacts`, [clientId]);
  const contacts = Array.isArray(data) ? data : (initialContacts ?? []);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [verifying, setVerifying] = useState(null);

  async function verifyRecall(contact) {
    setVerifying(contact.id);
    try {
      await apiFetch(`/clients/${clientId}/contacts/${contact.id}/verify-recall`, { method: 'POST', body: {} });
      refetch();
    } catch (_) {}
    setVerifying(null);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {showAdd && <ContactFormModal clientId={clientId} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); refetch(); }} />}
      {editing  && <ContactFormModal clientId={clientId} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); }} />}

      <SectionHeader label="Contacts" action={<Btn size="sm" onClick={() => setShowAdd(true)}>+ Add Contact</Btn>} />

      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>
      ) : !contacts.length ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No contacts yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {contacts.map(c => (
            <div key={c.id} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--pld-green)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                {initials(c.name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</span>
                  {c.role && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.role}</span>}
                  {c.is_primary        && <Pill on label="Primary" />}
                  {c.is_accounts       && <Pill on label="Accounts" />}
                  {c.is_recall_contact && <Pill on={c.recall_contact_verified} label={c.recall_contact_verified ? 'Recall ✓' : 'Recall — unverified'} />}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  {c.email && <span>✉ {c.email}</span>}
                  {c.phone && <span>📞 {c.phone}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {c.is_recall_contact && !c.recall_contact_verified && (
                  <Btn size="sm" variant="ghost" disabled={verifying === c.id} onClick={() => verifyRecall(c)}>
                    {verifying === c.id ? 'Verifying…' : 'Verify Recall'}
                  </Btn>
                )}
                <Btn size="sm" variant="ghost" onClick={() => setEditing(c)}>Edit</Btn>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Simple stock item typeahead ───────────────────────────────────────────────
function StockPicker({ value, search, onSearchChange, onSelect, items }) {
  const [open, setOpen] = useState(false);
  const filtered = (items ?? []).filter(s =>
    !search || s.name.toLowerCase().includes(search.toLowerCase()) || (s.sku ?? '').toLowerCase().includes(search.toLowerCase())
  ).slice(0, 10);
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 6 }}>
        <Input value={search} onChange={e => { onSearchChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Search stock items…" />
        {value && <button type="button" onClick={() => { onSelect(null); onSearchChange(''); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18, padding: '0 6px' }}>×</button>}
      </div>
      {open && filtered.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 400, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)', maxHeight: 200, overflowY: 'auto', marginTop: 2 }}>
          {filtered.map(s => (
            <div key={s.id} onMouseDown={() => { onSelect(s); setOpen(false); }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              <span style={{ fontWeight: 600 }}>{s.name}</span>
              <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>{s.sku}</span>
            </div>
          ))}
        </div>
      )}
      {value && <p style={{ margin: '3px 0 0', fontSize: 11, color: 'var(--pld-green)', fontWeight: 600 }}>✓ Selected</p>}
    </div>
  );
}

// ── Intake Detail ─────────────────────────────────────────────────────────────
function IntakeDetail({ clientId, intakeId, onBack }) {
  const { data: intakeData, loading, error, refetch } = useApi(`/clients/${clientId}/intakes/${intakeId}`, [intakeId]);
  const { data: stockData } = useApi('/stock');
  const { data: locData } = useApi('/warehouse/locations');
  const allStock = stockData?.stock_items ?? stockData ?? [];
  const allLocations = Array.isArray(locData) ? locData : (locData?.locations ?? []);

  const [lineForm, setLineForm]   = useState({ stock_item_id: '', quantity_expected: '', search: '' });
  const [addingLine, setAddingLine] = useState(false);
  const [lineSaving, setLineSaving] = useState(false);
  const [lineError, setLineError]   = useState('');

  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkInLines, setCheckInLines] = useState([]);
  const [checkInSaving, setCheckInSaving] = useState(false);
  const [checkInError, setCheckInError]   = useState('');
  const [checkInDutyPaid, setCheckInDutyPaid] = useState(false);

  const [discrepancyOpen, setDiscrepancyOpen] = useState(false);
  const [discNotes, setDiscNotes]             = useState('');
  const [discSaving, setDiscSaving]           = useState(false);

  const intake = intakeData;
  const lines  = intake?.lines ?? [];

  function openCheckIn() {
    setCheckInLines((lines).map(l => ({
      id: l.id,
      stock_item_name: l.stock_item_name,
      quantity_expected: l.quantity_expected,
      quantity_received: String(l.quantity_expected ?? ''),
      condition: 'good',
      condition_notes: '',
      location_id: l.location_id ?? '',
    })));
    setCheckInError('');
    setCheckInOpen(true);
  }

  async function handleAddLine(e) {
    e.preventDefault(); setLineSaving(true); setLineError('');
    try {
      await apiFetch(`/clients/${clientId}/intakes/${intakeId}/lines`, {
        method: 'POST',
        body: { stock_item_id: lineForm.stock_item_id, quantity_expected: parseFloat(lineForm.quantity_expected) },
      });
      setLineForm({ stock_item_id: '', quantity_expected: '', search: '' });
      setAddingLine(false);
      refetch();
    } catch (err) { setLineError(err.message); }
    finally { setLineSaving(false); }
  }

  async function handleRemoveLine(lineId) {
    if (!(await uiConfirm('Remove this line?'))) return;
    try {
      await apiFetch(`/clients/${clientId}/intakes/${intakeId}/lines/${lineId}`, { method: 'DELETE' });
      refetch();
    } catch (err) { alert(err.message); }
  }

  async function handleCheckIn(e) {
    e.preventDefault(); setCheckInSaving(true); setCheckInError('');
    try {
      await apiFetch(`/clients/${clientId}/intakes/${intakeId}/check-in`, {
        method: 'POST',
        body: {
          lines: checkInLines.map(l => ({
            id: l.id,
            quantity_received: parseFloat(l.quantity_received),
            condition: l.condition,
            condition_notes: l.condition_notes,
            location_id: l.location_id || undefined,
          })),
          duty_paid: checkInDutyPaid,
        },
      });
      setCheckInOpen(false);
      refetch();
    } catch (err) { setCheckInError(err.message); }
    finally { setCheckInSaving(false); }
  }

  async function handleDiscrepancy(e) {
    e.preventDefault(); setDiscSaving(true);
    try {
      await apiFetch(`/clients/${clientId}/intakes/${intakeId}/discrepancy`, {
        method: 'POST', body: { notes: discNotes },
      });
      setDiscrepancyOpen(false);
      refetch();
    } catch (err) { alert(err.message); }
    finally { setDiscSaving(false); }
  }

  if (loading) return <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>;
  if (error || !intake) return <p style={{ color: '#dc2626', fontSize: 14 }}>Failed to load intake.</p>;

  const isPending = intake.status === 'pending' || intake.status === 'discrepancy';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Check-in modal */}
      {checkInOpen && (
        <Modal title="Check In Stock" onClose={() => setCheckInOpen(false)} width={600}>
          <form onSubmit={handleCheckIn} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>Enter quantities received and condition for each line.</p>
            {checkInLines.map((l, i) => (
              <div key={l.id} style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{l.stock_item_name}
                  <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 13, marginLeft: 8 }}>expected {l.quantity_expected}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <Field label="Qty received">
                    <Input type="number" min="0" step="0.001" required value={l.quantity_received}
                      onChange={e => setCheckInLines(ls => ls.map((x, j) => j === i ? { ...x, quantity_received: e.target.value } : x))} />
                  </Field>
                  <Field label="Condition">
                    <SelectEl value={l.condition}
                      onChange={e => setCheckInLines(ls => ls.map((x, j) => j === i ? { ...x, condition: e.target.value } : x))}>
                      <option value="good">Good</option>
                      <option value="damaged">Damaged</option>
                      <option value="rejected">Rejected</option>
                    </SelectEl>
                  </Field>
                </div>
                <Field label="Location">
                  <LocationSelect locations={allLocations} value={l.location_id}
                    onChange={e => setCheckInLines(ls => ls.map((x, j) => j === i ? { ...x, location_id: e.target.value } : x))} placeholder="Select location…" />
                </Field>
                {l.condition !== 'good' && (
                  <Field label="Condition notes">
                    <Input value={l.condition_notes}
                      onChange={e => setCheckInLines(ls => ls.map((x, j) => j === i ? { ...x, condition_notes: e.target.value } : x))}
                      placeholder="Describe the issue…" />
                  </Field>
                )}
              </div>
            ))}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={checkInDutyPaid} onChange={e => setCheckInDutyPaid(e.target.checked)} style={{ marginTop: 2 }} />
              <span><strong>This stock arrives duty paid</strong> — duty has already been paid on it, so it isn't a bonded receipt and doesn't go on the W1. Leave unticked for stock arriving in bond.</span>
            </label>
            {checkInError && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{checkInError}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn type="button" variant="ghost" onClick={() => setCheckInOpen(false)}>Cancel</Btn>
              <Btn type="submit" disabled={checkInSaving}>{checkInSaving ? 'Checking in…' : 'Confirm Check-in'}</Btn>
            </div>
          </form>
        </Modal>
      )}

      {/* Discrepancy modal */}
      {discrepancyOpen && (
        <Modal title="Flag Discrepancy" onClose={() => setDiscrepancyOpen(false)} width={440}>
          <form onSubmit={handleDiscrepancy} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Field label="Notes *">
              <Textarea required value={discNotes} onChange={e => setDiscNotes(e.target.value)} autoFocus placeholder="Describe the discrepancy…" />
            </Field>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn type="button" variant="ghost" onClick={() => setDiscrepancyOpen(false)}>Cancel</Btn>
              <Btn type="submit" variant="danger" disabled={discSaving}>{discSaving ? 'Saving…' : 'Flag Discrepancy'}</Btn>
            </div>
          </form>
        </Modal>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0 }}>←</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 16 }}>{intake.intake_ref}</span>
            <StatusBadge status={intake.status} colours={INTAKE_STATUS_COLOURS} />
            {intake.discrepancy_noted && <span style={{ fontSize: 12, fontWeight: 700, color: '#dc2626' }}>⚠ Discrepancy noted</span>}
          </div>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
            {intake.received_at ? `Received ${fmtDate(intake.received_at)}` : 'Not yet received'}
            {intake.notes ? ` · ${intake.notes}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isPending && lines.length > 0 && (
            <Btn size="sm" onClick={openCheckIn}>✓ Check In</Btn>
          )}
          {!isPending && !intake.discrepancy_noted && (
            <Btn size="sm" variant="ghost" onClick={() => setDiscrepancyOpen(true)}>⚠ Flag Discrepancy</Btn>
          )}
        </div>
      </div>

      {/* Lines */}
      <div>
        <SectionHeader label="Stock Items" action={isPending && (
          <Btn size="sm" onClick={() => setAddingLine(v => !v)}>{addingLine ? 'Cancel' : '+ Add Item'}</Btn>
        )} />

        {/* Add line form */}
        {addingLine && (
          <form onSubmit={handleAddLine} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
              <Field label="Stock item *">
                <StockPicker
                  items={allStock}
                  value={lineForm.stock_item_id}
                  search={lineForm.search}
                  onSearchChange={v => setLineForm(f => ({ ...f, search: v, stock_item_id: '' }))}
                  onSelect={item => setLineForm(f => ({ ...f, stock_item_id: item?.id ?? '', search: item?.name ?? '' }))}
                />
              </Field>
              <Field label="Expected qty">
                <Input required type="number" min="0" step="0.001" value={lineForm.quantity_expected}
                  onChange={e => setLineForm(f => ({ ...f, quantity_expected: e.target.value }))} placeholder="e.g. 200" />
              </Field>
            </div>
            {lineError && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{lineError}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn type="button" variant="ghost" size="sm" onClick={() => setAddingLine(false)}>Cancel</Btn>
              <Btn type="submit" size="sm" disabled={lineSaving || !lineForm.stock_item_id || !lineForm.quantity_expected}>
                {lineSaving ? 'Adding…' : 'Add Item'}
              </Btn>
            </div>
          </form>
        )}

        {lines.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>
            {isPending ? 'No items added yet — use "Add Item" to add stock.' : 'No line items recorded.'}
          </p>
        ) : (
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
                  {['Stock Item', 'SKU', 'Expected', 'Received', 'Condition', isPending ? '' : 'Notes'].map(h => (
                    <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l.id} style={{ borderBottom: i < lines.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--text)' }}>{l.stock_item_name}</td>
                    <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{l.sku ?? '—'}</td>
                    <td style={{ padding: '10px 14px', fontFamily: 'monospace' }}>{l.quantity_expected}</td>
                    <td style={{ padding: '10px 14px', fontFamily: 'monospace' }}>{l.quantity_received ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td style={{ padding: '10px 14px' }}>
                      {l.condition === 'good'     && <span style={{ color: '#166534', fontSize: 12, fontWeight: 600 }}>Good</span>}
                      {l.condition === 'damaged'  && <span style={{ color: '#c2410c', fontSize: 12, fontWeight: 600 }}>Damaged</span>}
                      {l.condition === 'rejected' && <span style={{ color: '#dc2626', fontSize: 12, fontWeight: 600 }}>Rejected</span>}
                      {!l.condition              && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                    </td>
                    {isPending ? (
                      <td style={{ padding: '10px 14px' }}>
                        <button onClick={() => handleRemoveLine(l.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 16, lineHeight: 1 }}>×</button>
                      </td>
                    ) : (
                      <td style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-muted)' }}>{l.condition_notes || '—'}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Intakes Tab ───────────────────────────────────────────────────────────────
function IntakesTab({ clientId }) {
  const { data, loading, refetch } = useApi(`/clients/${clientId}/intakes`, [clientId]);
  const intakes = Array.isArray(data) ? data : (data?.data ?? []);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedIntakeId, setSelectedIntakeId] = useState(null);

  async function handleCreate(notes) {
    const res = await apiFetch(`/clients/${clientId}/intakes`, { method: 'POST', body: { notes } });
    setShowCreate(false);
    refetch();
    setSelectedIntakeId(res.id);
  }

  if (selectedIntakeId) {
    return <IntakeDetail clientId={clientId} intakeId={selectedIntakeId} onBack={() => { setSelectedIntakeId(null); refetch(); }} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {showCreate && <CreateIntakeModal clientId={clientId} onClose={() => setShowCreate(false)} onCreated={handleCreate} />}
      <SectionHeader label="Stock Intakes" action={<Btn size="sm" onClick={() => setShowCreate(true)}>+ New Intake</Btn>} />
      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>
      ) : !intakes.length ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No intakes recorded yet.</p>
      ) : (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)', background: 'var(--surface-2)' }}>
                {['Ref', 'Status', 'Items', 'Received', 'Discrepancy', 'Notes', ''].map(h => (
                  <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {intakes.map((i, idx) => (
                <tr key={i.id} onClick={() => setSelectedIntakeId(i.id)}
                  style={{ borderBottom: idx < intakes.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ padding: '9px 14px', fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{i.intake_ref}</td>
                  <td style={{ padding: '9px 14px' }}><StatusBadge status={i.status} colours={INTAKE_STATUS_COLOURS} /></td>
                  <td style={{ padding: '9px 14px', color: 'var(--text-muted)', fontSize: 13 }}>{i.line_count ?? '—'}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--text-muted)' }}>{fmtDate(i.received_at)}</td>
                  <td style={{ padding: '9px 14px' }}>
                    {i.discrepancy_noted
                      ? <span style={{ color: '#dc2626', fontSize: 12, fontWeight: 600 }}>⚠ Yes</span>
                      : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                  </td>
                  <td style={{ padding: '9px 14px', color: 'var(--text-muted)', fontSize: 13 }}>{i.notes || '—'}</td>
            
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Storage report PDF ────────────────────────────────────────────────────────
async function viewStorageReportPdf(clientId) {
  const token = localStorage.getItem('pld_token');
  const res = await fetch(`/api/clients/${clientId}/storage/report.pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to load storage report');
  const blob = await res.blob();

  // A plain blob: URL has no filename, so Chrome's PDF viewer falls back to
  // "document" when you hit Save. Wrapping the blob in a File — using the
  // filename the server sent via Content-Disposition — carries a real name
  // through to the save dialog. (Blob object URLs don't preserve response
  // headers, which is also why we can't just point window.open at the
  // fetch URL directly for an authenticated request.)
  const disposition = res.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const filename = match ? match[1] : `Storage-Report-${clientId}.pdf`;
  const file = new File([blob], filename, { type: 'application/pdf' });

  const url = URL.createObjectURL(file);
  window.open(url, '_blank');
}

// ── Storage Tab ────────────────────────────────────────────────────────────────
function StorageTab({ clientId }) {
  const { data, loading, error } = useApi(`/clients/${clientId}/storage`, [clientId]);
  const units = data?.units ?? [];
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  async function handleDownload() {
    setDownloading(true); setDownloadError('');
    try {
      await viewStorageReportPdf(clientId);
    } catch (e) {
      setDownloadError(e.message || 'Could not generate report');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionHeader
        label="Storage Units"
        action={
          <Btn size="sm" onClick={handleDownload} disabled={downloading || !units.length}>
            {downloading ? 'Generating…' : '⬇ Download Report'}
          </Btn>
        }
      />
      {downloadError && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{downloadError}</p>}
      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>
      ) : error ? (
        <p style={{ color: '#dc2626', fontSize: 14 }}>{error}</p>
      ) : !units.length ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No storage units are currently assigned to this client.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {units.map(unit => (
            <div key={unit.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: 'var(--surface-2)', flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13 }}>{unit.unit_ref}</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'capitalize' }}>{unit.unit_type}</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {[unit.zone_name, unit.location_code].filter(Boolean).join(' — ') || 'No location'}
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'capitalize' }}>{(unit.duty_status || '').replace(/_/g, ' ')}</span>
              </div>
              {unit.contents.length === 0 ? (
                <p style={{ margin: 0, padding: '10px 16px', fontSize: 13, color: 'var(--text-muted)' }}>No contents recorded.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['Item', 'SKU', 'Qty', 'Unit'].map(h => (
                        <th key={h} style={{ padding: '7px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {unit.contents.map((c, idx) => (
                      <tr key={idx} style={{ borderBottom: idx < unit.contents.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <td style={{ padding: '7px 16px' }}>{c.name}</td>
                        <td style={{ padding: '7px 16px', color: 'var(--text-muted)' }}>{c.sku}</td>
                        <td style={{ padding: '7px 16px' }}>{c.quantity}</td>
                        <td style={{ padding: '7px 16px', color: 'var(--text-muted)' }}>{c.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Distilling Services Agreements ───────────────────────────────────────────
// Filled in from the client, issued as a Word file, kept here with the signed
// copy once it's back (Joe, 29 Oct 2026).
async function downloadAuthed(url, fallbackName, options = {}) {
  const res = await fetch(`/api${url}`, { ...options, headers: { Authorization: `Bearer ${getToken()}`, ...(options.headers || {}) } });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Download failed'); }
  const blob = await res.blob();
  const m = (res.headers.get('content-disposition') || '').match(/filename="?([^";]+)"?/i);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = m ? m[1] : fallbackName;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}

const AGREEMENT_SECTIONS = [
  ['The client', [
    ['party_type', 'Agreement with', 'party'], ['client_name', 'Name'], ['company_number', 'Company number'],
    ['registered_office', 'Registered office', 'wide'], ['agreement_date', 'Agreement date', 'date'],
  ]],
  ['Schedule 1 — Recipe development', [
    ['s1_product', 'Product'], ['development_cost', 'Recipe development cost', 'wide'],
    ['dev_start_days', 'Development starts within (days of payment)'], ['samples', 'Samples (up to)'],
    ['first_sample_weeks', 'First sample within (weeks)'],
    ['samples_note', 'Extra terms on samples (optional)', 'wide'],
    ['concept_brief', 'Concept brief (Appendix 1.1)', 'text'],
  ], 'development'],
  ['Schedule 2 — Production', [
    ['s2_product', 'Product'], ['quantity', 'Quantity'],
    ['s2_description', 'Product description', 'text'],
    ['materials', 'Materials provided by the client'], ['packaging', 'Packaging'],
    ['commencement_date', 'Production commencement date', 'date'], ['duty_by_pld', 'Duty paid by PLD?', 'yn'],
  ], 'bulk'],
  ['Appendix 2.1 — Prices', [
    ['dist_volume', 'Distillation volume (litres)'], ['dist_price', 'Distillation price (£)'],
    ['bottling_day_rate', 'Bottling price per day (£)'], ['cask_rate', 'Casks (£)'],
    ['rate_bonded', 'Duty suspended pallets (£/week)'], ['rate_unbonded', 'Duty paid pallets (£/week)'],
    ['rate_packaging', 'Packaging pallets (£/week)'],
  ], 'bulk'],
  ['Schedule 3 — Consulting', [
    ['svc_description', 'Service description', 'wide'], ['svc_days', 'Estimated days'],
    ['svc_provider', 'Service provider'], ['svc_day_rate', 'Day rate (£)'],
  ], 'consulting'],
];

const AGREEMENT_KINDS = { development: 'Development agreement', bulk: 'Bulk agreement', consulting: 'Consulting agreement' };
const AGREEMENT_KIND_NOTE = {
  development: 'Schedule 1 — recipe development',
  bulk: 'Schedule 2 and Appendix 2.1 — production and prices',
  consulting: 'Schedule 3 — consulting services',
};
const kindOf = (d = {}) => d.kind || null;

function NewAgreementModal({ clientId, kind, onClose, onSaved }) {
  const { data: start, loading } = useApi(`/clients/${clientId}/agreements/prefill?kind=${kind}`, [clientId, kind]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const f = form || start || {};
  const set = (k, v) => setForm({ ...f, [k]: v });
  const total = Number(f.svc_days) * Number(String(f.svc_day_rate || '').replace(/[£,]/g, ''));

  async function issue() {
    setBusy('issue'); setErr('');
    try {
      const r = await apiFetch(`/clients/${clientId}/agreements`, { method: 'POST', body: { ...f, kind } });
      await downloadAuthed(`/clients/${clientId}/agreements/${r.data.id}/file`, `${r.data.ref}.docx`);
      onSaved();
    } catch (e) { setErr(e.message); setBusy(''); }
  }
  async function draft() {
    setBusy('draft'); setErr('');
    try {
      await downloadAuthed(`/clients/${clientId}/agreements/preview`, 'Distilling Services Agreement (draft).docx',
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...f, kind }) });
    } catch (e) { setErr(e.message); }
    setBusy('');
  }

  return (
    <Modal title={`New ${AGREEMENT_KINDS[kind].toLowerCase()}`} onClose={onClose} width={760}>
      {loading && !start ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
            The full Distilling Services Agreement with {AGREEMENT_KIND_NOTE[kind]}. Filled in from the client and their last {AGREEMENT_KINDS[kind].toLowerCase()}; anything left blank stays blank.
          </p>
          {AGREEMENT_SECTIONS.filter(([, , k]) => !k || k === kind).map(([title, fields]) => (
            <div key={title}>
              <SectionHeader label={title} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                {fields.filter(([k]) => !(k === 'company_number' && f.party_type === 'individual')).map(([k, l, kind]) => {
                  const label = k === 'registered_office' && f.party_type === 'individual' ? 'Address' : l;
                  return (
                  <div key={k} style={{ gridColumn: kind === 'text' || kind === 'wide' ? '1 / -1' : undefined }}>
                    <Field label={label} hint={k === 'development_cost' ? 'e.g. 5000 — the agreement adds "plus VAT at prevailing rates, payable in full in advance"' : undefined}>
                      {kind === 'party' ? (
                          <SelectEl value={f[k] || 'company'} onChange={e => set(k, e.target.value)}>
                            <option value="company">A company</option><option value="individual">An individual</option>
                          </SelectEl>
                        ) : kind === 'text' ? <Textarea value={f[k] || ''} onChange={e => set(k, e.target.value)} />
                        : kind === 'yn' ? (
                          <SelectEl value={f[k] || 'No'} onChange={e => set(k, e.target.value)}>
                            <option>Yes</option><option>No</option>
                          </SelectEl>
                        ) : <Input type={kind === 'date' ? 'date' : 'text'} value={f[k] || ''} onChange={e => set(k, e.target.value)} />}
                    </Field>
                  </div>
                  );
                })}
                {title.startsWith('Schedule 3') && Number.isFinite(total) && total > 0 && (
                  <div style={{ gridColumn: '1 / -1', fontSize: 13, color: 'var(--text-muted)' }}>
                    Total project cost: <strong style={{ color: 'var(--text)' }}>£{total.toLocaleString('en-GB', { maximumFractionDigits: 2 })}</strong>
                  </div>
                )}
              </div>
            </div>
          ))}
          {err && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{err}</p>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn variant="ghost" onClick={draft} disabled={!!busy}>{busy === 'draft' ? 'Making…' : 'Download a draft'}</Btn>
            <Btn onClick={issue} disabled={!!busy || !f.client_name}>{busy === 'issue' ? 'Issuing…' : 'Issue & download'}</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

// Draw a signature once; it's kept on your user for next time (Joe, 2 Nov 2026).
function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [empty, setEmpty] = useState(true);
  const pos = e => { const r = ref.current.getBoundingClientRect(); const t = e.touches?.[0] || e; return [(t.clientX - r.left) * (ref.current.width / r.width), (t.clientY - r.top) * (ref.current.height / r.height)]; };
  const start = e => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = e => {
    if (!drawing.current) return; e.preventDefault();
    const ctx = ref.current.getContext('2d'); const p = pos(e);
    ctx.strokeStyle = '#14204d'; ctx.lineWidth = 3; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.beginPath(); ctx.moveTo(...last.current); ctx.lineTo(...p); ctx.stroke();
    last.current = p; setEmpty(false);
  };
  const end = () => { if (!drawing.current) return; drawing.current = false; onChange(trimmedPng(ref.current)); };
  const clear = () => { ref.current.getContext('2d').clearRect(0, 0, ref.current.width, ref.current.height); setEmpty(true); onChange(null); };
  return (
    <div>
      <canvas ref={ref} width={600} height={180}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        style={{ width: '100%', height: 150, border: '1px dashed var(--border)', borderRadius: 'var(--radius)', background: '#fff', touchAction: 'none', cursor: 'crosshair' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)' }}>
        <span>{empty ? 'Sign in the box with your mouse or finger' : 'Signed'}</span>
        <button type="button" onClick={clear} style={{ border: 'none', background: 'none', color: 'var(--pld-green)', cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}>Clear</button>
      </div>
    </div>
  );
}
// The drawn part only, as a PNG.
function trimmedPng(canvas) {
  const ctx = canvas.getContext('2d'); const { width: w, height: h } = canvas;
  const px = ctx.getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (px[(y * w + x) * 4 + 3] > 0) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  if (x1 < 0) return null;
  const pad = 6; const out = document.createElement('canvas');
  out.width = x1 - x0 + pad * 2; out.height = y1 - y0 + pad * 2;
  out.getContext('2d').drawImage(canvas, x0 - pad, y0 - pad, out.width, out.height, 0, 0, out.width, out.height);
  return out.toDataURL('image/png');
}

function SignSendModal({ clientId, agreement, contacts, onClose, onDone }) {
  const { data: me } = useApi(`/clients/${clientId}/agreements/my-signature`, [clientId]);
  const [f, setF] = useState(null);
  const [redraw, setRedraw] = useState(false);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const withEmail = (contacts || []).filter(c => c.email);
  if (me && !f) {
    const primary = withEmail.find(c => c.is_primary) || withEmail[0];
    setF({ name: me.name, position: me.position, date: today, signature_png: me.signature_png, save_signature: true, to: primary ? [primary.email] : [], extra: '', message: '' });
  }
  if (!f) return <Modal title={`Sign ${agreement.ref}`} onClose={onClose}><p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p></Modal>;
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const toggle = email => set('to', f.to.includes(email) ? f.to.filter(e => e !== email) : [...f.to, email]);
  const recipients = [...f.to, ...f.extra.split(/[,;\s]+/).filter(Boolean)];
  const showPad = redraw || !me.signature_png;

  async function go(send) {
    setBusy(send ? 'send' : 'sign'); setErr('');
    try {
      if (!f.signature_png) throw new Error('Draw your signature first');
      await apiFetch(`/clients/${clientId}/agreements/${agreement.id}/sign`, { method: 'POST', body: { ...f, to: send ? recipients : [] } });
      if (!send) await downloadAuthed(`/clients/${clientId}/agreements/${agreement.id}/file?pld=1`, `${agreement.ref} signed.pdf`);
      onDone();
    } catch (e) { setErr(e.message); setBusy(''); }
  }

  return (
    <Modal title={`Sign ${agreement.ref} for Pleasant Land Distillery`} onClose={onClose} width={620}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
          Your signature, name, position and date go on our signature block{agreement.source === 'uploaded' ? ' in their document' : ''}, and on our "Agreed for" lines in the schedules. The client's block is left for them.
        </p>
        <Field label="Signature">
          {showPad ? <SignaturePad onChange={png => set('signature_png', png)} /> : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 10, background: '#fff' }}>
              <img src={f.signature_png} alt="Your signature" style={{ maxHeight: 60, maxWidth: 260 }} />
              <span style={{ flex: 1 }} />
              <Btn size="sm" variant="ghost" type="button" onClick={() => { setRedraw(true); set('signature_png', null); }}>Draw again</Btn>
            </div>
          )}
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Field label="Name"><Input value={f.name} onChange={e => set('name', e.target.value)} /></Field>
          <Field label="Position"><Input value={f.position} onChange={e => set('position', e.target.value)} placeholder="e.g. Operations Director" /></Field>
          <Field label="Date"><Input value={f.date} onChange={e => set('date', e.target.value)} /></Field>
        </div>
        <label style={{ display: 'flex', gap: 6, fontSize: 13, alignItems: 'center' }}>
          <input type="checkbox" checked={f.save_signature} onChange={e => set('save_signature', e.target.checked)} /> Remember my signature and position for next time
        </label>
        <Field label="Email the signed copy to">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {withEmail.map(c => (
              <label key={c.id || c.email} style={{ display: 'flex', gap: 6, fontSize: 13, alignItems: 'center' }}>
                <input type="checkbox" checked={f.to.includes(c.email)} onChange={() => toggle(c.email)} /> {c.name} <span style={{ color: 'var(--text-muted)' }}>{c.email}{c.role ? ` · ${c.role}` : ''}</span>
              </label>
            ))}
            {!withEmail.length && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>No contacts with an email on this client. Add one on the Contacts tab, or type an address below.</span>}
            <Input value={f.extra} onChange={e => set('extra', e.target.value)} placeholder="Other email addresses, separated by commas" />
          </div>
        </Field>
        <Field label="Message" hint="Leave blank for the standard note asking them to sign and send a copy back">
          <Textarea value={f.message} onChange={e => set('message', e.target.value)} rows={4} />
        </Field>
        {err && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{err}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn variant="ghost" type="button" disabled={!!busy || !f.name} onClick={() => go(false)}>{busy === 'sign' ? 'Signing…' : 'Sign only (download)'}</Btn>
          <Btn type="button" disabled={!!busy || !f.name || !recipients.length} onClick={() => go(true)}>{busy === 'send' ? 'Sending…' : `Sign & email${recipients.length ? ` (${recipients.length})` : ''}`}</Btn>
        </div>
      </div>
    </Modal>
  );
}

function UploadAgreementModal({ clientId, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [kind, setKind] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  async function save() {
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('kind', kind); fd.append('title', title);
      const res = await fetch(`/api/clients/${clientId}/agreements/upload`, { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const b = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(b.error || 'Upload failed');
      onSaved();
    } catch (e) { setErr(e.message); setBusy(false); }
  }
  return (
    <Modal title="Upload an agreement" onClose={onClose} width={520}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>For an agreement agreed outside Blake, e.g. a client's revised version. It's kept on the client and can be signed and sent like the others.</p>
        <Field label="Agreement file" hint="PDF or Word (.docx)">
          <input type="file" accept=".pdf,.docx,application/pdf" onChange={e => setFile(e.target.files?.[0] || null)} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Kind">
            <SelectEl value={kind} onChange={e => setKind(e.target.value)}>
              <option value="">Other / mixed</option>
              {Object.entries(AGREEMENT_KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </SelectEl>
          </Field>
          <Field label="Title (optional)"><Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Revised agreement" /></Field>
        </div>
        {err && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{err}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="button" disabled={busy || !file} onClick={save}>{busy ? 'Uploading…' : 'Upload'}</Btn>
        </div>
      </div>
    </Modal>
  );
}

function AgreementsTab({ clientId }) {
  const { data, loading, error, refetch } = useApi(`/clients/${clientId}/agreements`, [clientId]);
  const rows = data ?? [];
  const [creating, setCreating] = useState(null);
  const [msg, setMsg] = useState('');
  const [uploading, setUploading] = useState(false);
  const [signing, setSigning] = useState(null);
  const { data: client } = useApi(`/clients/${clientId}`, [clientId]);

  async function attach(a, file) {
    if (!file) return;
    setMsg('');
    const fd = new FormData(); fd.append('file', file);
    const res = await fetch(`/api/clients/${clientId}/agreements/${a.id}/signed`, { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
    if (!res.ok) { const e = await res.json().catch(() => ({})); setMsg(e.error || 'Could not attach the signed copy'); return; }
    refetch();
  }
  async function remove(a) {
    if (!(await uiConfirm(`Delete ${a.ref}? It hasn't been signed.`))) return;
    try { await apiFetch(`/clients/${clientId}/agreements/${a.id}`, { method: 'DELETE' }); refetch(); } catch (e) { setMsg(e.message); }
  }
  const dl = (a, which) => downloadAuthed(`/clients/${clientId}/agreements/${a.id}/file${which === 'signed' ? '?signed=1' : which === 'pld' ? '?pld=1' : ''}`,
    which === 'signed' ? a.signed_file_name : which === 'pld' ? a.pld_signed_file_name : a.file_name).catch(e => setMsg(e.message));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {uploading && <UploadAgreementModal clientId={clientId} onClose={() => setUploading(false)} onSaved={() => { setUploading(false); refetch(); }} />}
      {signing && <SignSendModal clientId={clientId} agreement={signing} contacts={client?.contacts} onClose={() => setSigning(null)} onDone={() => { setSigning(null); refetch(); }} />}
      {creating && <NewAgreementModal clientId={clientId} kind={creating} onClose={() => setCreating(null)} onSaved={() => { setCreating(null); refetch(); }} />}
      <SectionHeader label="Distilling Services Agreements" action={
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {Object.entries(AGREEMENT_KINDS).map(([k, label]) => <Btn key={k} size="sm" variant={k === 'development' ? 'primary' : 'ghost'} onClick={() => setCreating(k)}>+ {label}</Btn>)}
          <Btn size="sm" variant="ghost" onClick={() => setUploading(true)}>Upload agreement</Btn>
        </div>
      } />
      {msg && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{msg}</p>}
      {loading && !data ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>
        : error ? <p style={{ color: '#dc2626', fontSize: 14 }}>{error}</p>
        : !rows.length ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No agreements yet. Each button fills one in from this client's details.</p>
        : (
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            {rows.map((a, i) => {
              const d = a.details || {};
              const k = kindOf(d);
              const what = [d.title, k ? AGREEMENT_KINDS[k] : null, a.source === 'uploaded' ? 'uploaded' : null, k === 'bulk' ? d.s2_product : k === 'consulting' ? d.svc_description : d.s1_product || d.s2_product, k === 'bulk' ? d.quantity : null].filter(Boolean).join(' · ');
              return (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderTop: i ? '1px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{a.ref} {what && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>— {what}</span>}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      {a.source === 'uploaded' ? 'Uploaded' : 'Issued'} {fmtDate(a.created_at)}
                      {a.pld_signed_at ? ` · Signed by ${a.pld_signed_by_name || 'us'} ${fmtDate(a.pld_signed_at)}` : ''}
                      {a.sent_at ? ` · Emailed to ${a.sent_to} ${fmtDate(a.sent_at)}` : ''}
                      {a.signed_at ? ` · Countersigned copy added ${fmtDate(a.signed_at)}` : ''}
                    </div>
                  </div>
                  <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: a.signed_at ? '#dcfce7' : a.pld_signed_at ? '#dbeafe' : '#fef3c7', color: a.signed_at ? '#166534' : a.pld_signed_at ? '#1e40af' : '#92400e' }}>
                    {a.signed_at ? 'Signed' : a.pld_signed_at ? 'Signed by us — with client' : 'Not signed yet'}
                  </span>
                  <Btn size="sm" variant="ghost" onClick={() => dl(a, 'issued')}>{/pdf/i.test(a.file_type || '') ? 'PDF' : 'Word file'}</Btn>
                  {!a.signed_at && (a.pld_signed_at
                    ? <Btn size="sm" variant="ghost" onClick={() => dl(a, 'pld')}>Our signed copy</Btn>
                    : <Btn size="sm" onClick={() => setSigning(a)}>Sign &amp; send</Btn>)}
                  {a.signed_at
                    ? <Btn size="sm" variant="ghost" onClick={() => dl(a, 'signed')}>Signed copy</Btn>
                    : (
                      <label style={{ fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 'var(--radius)', background: 'var(--pld-green)', color: '#fff', cursor: 'pointer' }}>
                        Add signed copy
                        <input type="file" accept=".pdf,.doc,.docx,image/*" style={{ display: 'none' }} onChange={e => { attach(a, e.target.files?.[0]); e.target.value = ''; }} />
                      </label>
                    )}
                  {!a.signed_at && !a.pld_signed_at && <Btn size="sm" variant="ghost" onClick={() => remove(a)} style={{ color: '#dc2626' }}>Delete</Btn>}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}

// ── Create Intake Modal ───────────────────────────────────────────────────────
function CreateIntakeModal({ clientId, onClose, onCreated }) {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try { await onCreated(notes); }
    catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <Modal title="New Stock Intake" onClose={onClose} width={440}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field label="Notes" hint="Delivery notes, supplier details, etc.">
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} autoFocus />
        </Field>
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Creating…' : 'Create & Add Items →'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Goods Intake Link (per-client) ────────────────────────────────────────────
function GoodsIntakeLinkCard({ client, onChanged }) {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [newLink, setNewLink] = useState(null); // { path } — shown once, right after generating
  const [copied, setCopied] = useState(false);

  async function generate() {
    setWorking(true); setError('');
    try {
      const result = await apiFetch(`/clients/${client.id}/goods-intake-link`, { method: 'POST' });
      setNewLink(result);
      onChanged?.();
    } catch (e) {
      setError(e.message || 'Failed to generate link');
    } finally {
      setWorking(false);
    }
  }

  async function revoke() {
    if (!(await uiConfirm(`Revoke ${client.name}'s current goods intake link? Any copy they have saved will stop working immediately.`))) return;
    setWorking(true); setError('');
    try {
      await apiFetch(`/clients/${client.id}/goods-intake-link`, { method: 'DELETE' });
      setNewLink(null);
      onChanged?.();
    } catch (e) {
      setError(e.message || 'Failed to revoke link');
    } finally {
      setWorking(false);
    }
  }

  function copyNewLink() {
    const url = `${window.location.origin}${newLink.path}`;
    navigator.clipboard?.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <SectionHeader label="Goods Intake Link" />
      <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {newLink ? (
          <>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text)' }}>
              Here's {client.name}'s link — copy it now and send it to them. For security it won't be shown again after you leave this page.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <code style={{ flex: 1, minWidth: 220, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '7px 10px', fontSize: 12, color: 'var(--text)', wordBreak: 'break-all' }}>
                {window.location.origin}{newLink.path}
              </code>
              <Btn size="sm" variant="ghost" onClick={copyNewLink}>{copied ? '✓ Copied' : 'Copy link'}</Btn>
            </div>
          </>
        ) : client.has_goods_intake_link ? (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
            {client.name} has an active goods intake link. The raw link can't be retrieved again — generate a new one (which revokes the old one) if they've lost it.
          </p>
        ) : (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
            {client.name} doesn't have a goods intake link yet. Generate one to give them a private form for submitting deliveries — it won't show them any of our other clients.
          </p>
        )}
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn size="sm" onClick={generate} disabled={working}>
            {working ? 'Working…' : client.has_goods_intake_link ? 'Revoke & regenerate' : 'Generate link'}
          </Btn>
          {client.has_goods_intake_link && !newLink && (
            <Btn size="sm" variant="danger" onClick={revoke} disabled={working}>Revoke</Btn>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Client Detail ─────────────────────────────────────────────────────────────
function ClientDetail({ clientId, onBack, onEdited }) {
  const { data: client, loading, refetch } = useApi(`/clients/${clientId}`, [clientId]);
  const [tab, setTab] = useState('Details');
  const [editing, setEditing] = useState(false);

  if (loading && !client) return <p style={{ padding: 24, color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>;
  if (!client) return <p style={{ padding: 24, color: '#dc2626', fontSize: 14 }}>Client not found.</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {editing && (
        <ClientFormModal
          title="Edit Client"
          initial={client}
          onClose={() => setEditing(false)}
          onSaved={updated => { setEditing(false); refetch(); onEdited?.(updated); }}
        />
      )}

      {/* Header */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 22, lineHeight: 1, padding: 0 }}>←</button>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{client.name}</h2>
          <div style={{ display: 'flex', gap: 14, marginTop: 4, flexWrap: 'wrap', fontSize: 13, color: 'var(--text-muted)' }}>
            {client.excise_id    && <span>Excise: {client.excise_id}</span>}
            {client.vat_number   && <span>VAT: {client.vat_number}</span>}
            {client.company_number && <span>Co: {client.company_number}</span>}
            <Pill on={client.is_active !== false} label={client.is_active !== false ? 'Active' : 'Inactive'} />
            {client.nda_on_file         && <Pill on label="NDA" />}
            {client.is_own_duty_payer   && <Pill on label="Own duty payer" />}
            {client.show_costs_to_client && <Pill on label="Costs visible" />}
          </div>
        </div>
        <Btn size="sm" variant="ghost" onClick={() => setEditing(true)}>Edit</Btn>
      </div>

      {/* Tabs */}
      <div style={{ padding: '0 24px' }}>
        <TabBar tabs={['Details', 'Contacts', 'Intakes', 'Storage', 'Agreements']} active={tab} onChange={setTab} />
      </div>

      <div style={{ padding: '0 24px 32px' }}>
        {tab === 'Details' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <SectionHeader label="Client Information" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
              {[
                ['Excise ID',     client.excise_id],
                ['VAT number',    client.vat_number],
                ['Company no.',   client.company_number],
                ['AWRS',          client.awrs_number],
                ['EORI',          client.eori_number],
                ['Billing address',  client.billing_address],
                ['Delivery address', client.delivery_address],
                ['Delivery hours & access', client.delivery_notes],
                ['Their bonded warehouse', [client.bonded_warehouse_name, client.warehouse_keeper_id && `Keeper ID ${client.warehouse_keeper_id}`].filter(Boolean).join(' · ')],
                ['Bank', [client.bank_iban && `IBAN ${client.bank_iban}`, client.bank_bic && `BIC ${client.bank_bic}`].filter(Boolean).join(' · ')],
                ['Added',         fmtDate(client.created_at)],
              ].map(([label, value]) => (
                <div key={label} style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: '12px 16px' }}>
                  <p style={{ margin: 0, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 4 }}>{label}</p>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: value ? 'var(--text)' : 'var(--text-muted)' }}>{value || '—'}</p>
                </div>
              ))}
            </div>
            <GoodsIntakeLinkCard client={client} onChanged={refetch} />
          </div>
        )}
        {tab === 'Contacts' && <ContactsTab clientId={clientId} initialContacts={client.contacts} />}
        {tab === 'Intakes'  && <IntakesTab  clientId={clientId} />}
        {tab === 'Storage'  && <StorageTab  clientId={clientId} />}
        {tab === 'Agreements' && <AgreementsTab clientId={clientId} />}
      </div>
    </div>
  );
}

// ── Client List ───────────────────────────────────────────────────────────────
function ClientList({ onSelectClient }) {
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const url = `/clients?is_active=${!showInactive}${search ? `&search=${encodeURIComponent(search)}` : ''}`;
  const { data, loading, error, refetch } = useApi(url, [search, showInactive]);
  const clients = Array.isArray(data) ? data : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {showCreate && (
        <ClientFormModal
          title="New Client"
          onClose={() => setShowCreate(false)}
          onSaved={(client) => { setShowCreate(false); refetch(); onSelectClient(client.id); }}
        />
      )}
      {showImport && (
        <ImportCustomerFormModal
          onClose={() => setShowImport(false)}
          onSaved={(client) => { setShowImport(false); refetch(); onSelectClient(client.id); }}
          onOpenExisting={(c) => { setShowImport(false); onSelectClient(c.id); }}
        />
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search clients…"
            style={{ padding: '8px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', color: 'var(--text)', width: 220 }}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Show inactive
          </label>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="ghost" onClick={downloadCustomerForm}>Download blank form</Btn>
          <Btn variant="ghost" onClick={() => setShowImport(true)}>Import customer form</Btn>
          <Btn onClick={() => setShowCreate(true)}>+ New Client</Btn>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#dc2626' }}>Failed to load clients.</div>
        ) : !clients.length ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
            {search ? `No clients matching "${search}"` : 'No clients yet — create one to get started.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)', background: 'var(--surface-2)' }}>
                {['Client', 'Company No.', 'Flags', ''].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clients.map(c => (
                <tr key={c.id} onClick={() => onSelectClient(c.id)} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: c.is_active ? 'var(--pld-green)' : '#9ca3af', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                        {initials(c.name)}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: 'var(--text)' }}>{c.name}</div>
                        {!c.is_active && <div style={{ fontSize: 11, color: '#6b7280' }}>Inactive</div>}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 13 }}>{c.company_number || '—'}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {c.nda_on_file       && <Pill on label="NDA" />}
                      {c.is_own_duty_payer && <Pill on label="Own Duty" />}
                    </div>
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', color: 'var(--text-muted)', fontSize: 18 }}>›</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && clients.length > 0 && (
          <div style={{ padding: '8px 14px', borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)', background: 'var(--surface-2)' }}>
            {clients.length} client{clients.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  );
}

const STOCK_CATEGORIES = [
  { value: 'raw_material',  label: 'Raw material' },
  { value: 'packaging',     label: 'Packaging' },
  { value: 'consumable',    label: 'Consumable' },
  { value: 'client_stock',  label: 'Client stock' },
  { value: 'semi_product',  label: 'Semi product' },
  { value: 'by_product',    label: 'By-product' },
];

const GOODS_INTAKE_STATUS_COLOURS = {
  pending:  { bg: '#fef9c3', text: '#854d0e' },
  approved: { bg: '#dcfce7', text: '#166534' },
  rejected: { bg: '#fee2e2', text: '#991b1b' },
};

// ── Goods Intake Review Modal ─────────────────────────────────────────────────
function GoodsIntakeReviewModal({ intake, onClose, onDone }) {
  const { data: stockData } = useApi('/stock');
  const allStock = stockData?.stock_items ?? stockData ?? [];

  const rawLines = Array.isArray(intake.lines) ? intake.lines : [];
  const [lines, setLines] = useState(() => rawLines.map(l => ({
    ...l,
    action: 'match',        // 'match' | 'create' | 'skip'
    stock_item_id: '',
    search: l.material_name || '',
    category: 'client_stock',
    unit: l.unit || '',
    name: l.material_name || '',
  })));
  const [reviewNotes, setReviewNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setLine = (i, patch) => setLines(ls => ls.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  async function approve() {
    setSaving(true); setError('');
    try {
      const kept = lines.filter(l => l.action !== 'skip');
      if (kept.length === 0) throw new Error('At least one line must be kept to generate a purchase order');
      for (const l of kept) {
        if (l.action === 'match' && !l.stock_item_id) throw new Error(`"${l.material_name}" needs a matching stock item, or set it to create one`);
        if (l.action === 'create' && (!l.name?.trim() || !l.category || !l.unit)) throw new Error(`"${l.material_name}" needs a name, category, and unit to create a new material`);
      }
      await apiFetch(`/goods-intake/${intake.id}/approve`, {
        method: 'POST',
        body: {
          review_notes: reviewNotes,
          lines: lines.map(l => ({
            action: l.action,
            stock_item_id: l.stock_item_id || undefined,
            name: l.name, category: l.category, unit: l.unit,
            quantity: l.quantity, batch_number: l.batch_number,
            pack_size: l.pack_size, dimensions: l.dimensions,
            supplier_stock_code: l.supplier_stock_code,
            best_before_date: l.best_before_date, notes: l.notes,
          })),
        },
      });
      onDone();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function reject() {
    setSaving(true); setError('');
    try {
      await apiFetch(`/goods-intake/${intake.id}/reject`, { method: 'POST', body: { review_notes: reviewNotes } });
      onDone();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  const Row = ({ label, value }) => value ? (
    <div style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 13, color: 'var(--text-muted)', width: 160, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: 'var(--text)' }}>{value}</span>
    </div>
  ) : null;

  return (
    <Modal title="Review Goods Intake" onClose={onClose} width={720}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <SectionHeader label="Submitted By" />
          <Row label="Client" value={intake.client_name} />
          <Row label="Name" value={intake.submitted_by_name} />
          <Row label="Email" value={intake.submitted_by_email} />
          <Row label="Phone" value={intake.submitted_by_phone} />
          <Row label="Delivery location" value={intake.delivery_location} />
          <Row label="Expected delivery" value={fmtDate(intake.expected_delivery_date)} />
          <Row label="Submitted" value={fmtDate(intake.created_at)} />
        </div>
        {intake.notes && (
          <div>
            <SectionHeader label="Notes from Submitter" />
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text)', background: 'var(--surface-2)', padding: '10px 14px', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>{intake.notes}</p>
          </div>
        )}

        <div>
          <SectionHeader label="Items — match each to a stock item, or create a new one" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {lines.map((l, i) => (
              <div key={i} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{l.material_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {[l.quantity && l.unit ? `${l.quantity} ${l.unit}` : l.quantity, l.pack_size, l.dimensions, l.supplier_stock_code, l.batch_number, l.best_before_date].filter(Boolean).join(' · ') || 'No further details supplied'}
                    </div>
                  </div>
                  <SelectEl value={l.action} onChange={e => setLine(i, { action: e.target.value })} style={{ maxWidth: 160 }}>
                    <option value="match">Match to existing</option>
                    <option value="create">Create new material</option>
                    <option value="skip">Skip this line</option>
                  </SelectEl>
                </div>

                {l.action === 'match' && (
                  <Field label="Stock item">
                    <StockPicker
                      items={allStock}
                      value={l.stock_item_id}
                      search={l.search}
                      onSearchChange={v => setLine(i, { search: v, stock_item_id: '' })}
                      onSelect={item => setLine(i, { stock_item_id: item?.id ?? '', search: item?.name ?? '' })}
                    />
                  </Field>
                )}

                {l.action === 'create' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
                    <Field label="Material name">
                      <Input value={l.name} onChange={e => setLine(i, { name: e.target.value })} />
                    </Field>
                    <Field label="Category">
                      <SelectEl value={l.category} onChange={e => setLine(i, { category: e.target.value })}>
                        {STOCK_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </SelectEl>
                    </Field>
                    <Field label="Unit">
                      <Input value={l.unit} onChange={e => setLine(i, { unit: e.target.value })} placeholder="kg, litres, units..." />
                    </Field>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <Field label="Review Notes">
          <Textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} placeholder="Optional notes..." style={{ minHeight: 60 }} />
        </Field>
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="button" variant="danger" onClick={reject} disabled={saving}>Reject</Btn>
          <Btn type="button" onClick={approve} disabled={saving}>
            {saving ? 'Saving...' : 'Approve & Create Draft PO'}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

// ── Goods Intake Tab ──────────────────────────────────────────────────────────
function GoodsIntakeTab() {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const { data, loading, error, refetch } = useApi(`/goods-intake?status=${statusFilter}`, [statusFilter]);
  const intakes = Array.isArray(data?.data) ? data.data : [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <SelectEl value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ maxWidth: 160 }}>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </SelectEl>
          {data?.meta && (
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {data.meta.total} submission{data.meta.total !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {/* Each client now has their own private intake link, generated on their
            Details tab — there's no longer a single shared form link to copy here. */}
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Links are generated per-client on each client's Details tab
        </span>
      </div>

      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--surface-2)' }}>
              {['Client', 'Submitted By', 'Items', 'Status', 'Date'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '1px solid var(--border)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</td></tr>
            )}
            {!loading && error && (
              <tr><td colSpan={5} style={{ padding: 32, textAlign: 'center', color: '#dc2626' }}>{error}</td></tr>
            )}
            {!loading && !error && intakes.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>No submissions</td></tr>
            )}
            {intakes.map(i => {
              const c = GOODS_INTAKE_STATUS_COLOURS[i.status] ?? GOODS_INTAKE_STATUS_COLOURS.pending;
              const isPending = i.status === 'pending';
              const lineCount = Array.isArray(i.lines) ? i.lines.length : 0;
              return (
                <tr
                  key={i.id}
                  onClick={() => isPending && setSelected(i)}
                  style={{ cursor: isPending ? 'pointer' : 'default', borderBottom: '1px solid var(--border)' }}
                  onMouseEnter={e => { if (isPending) e.currentTarget.style.background = 'var(--surface-2)'; }}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--text)' }}>{i.client_name}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{i.submitted_by_name}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{lineCount}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', background: c.bg, color: c.text }}>{i.status}</span>
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{fmtDate(i.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && (
        <GoodsIntakeReviewModal
          intake={selected}
          onClose={() => setSelected(null)}
          onDone={() => { setSelected(null); refetch(); }}
        />
      )}
    </div>
  );
}

// -- Default export --
export default function Clients() {
  const [tab, setTab] = useState('Clients');
  const [selectedId, setSelectedId] = useState(null);

  if (selectedId) {
    return <ClientDetail clientId={selectedId} onBack={() => setSelectedId(null)} />;
  }

  return (
    <div>
      <TabBar tabs={['Clients', 'Goods Intake']} active={tab} onChange={setTab} />
      {tab === 'Clients'      && <ClientList onSelectClient={setSelectedId} />}
      {tab === 'Goods Intake' && <GoodsIntakeTab />}
    </div>
  );
}
